import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useTheme from 'hooks/useTheme';
import AntButton from 'components/AntButton/AntButton';
import { Modal, TextField } from 'components';

export const MIN_REASON_LENGTH = 10;
export const MAX_REASON_LENGTH = 500;

export const getNoPayRestrictionThemeTokens = theme => ({
	textPrimary: theme._highEmphasisColor,
	textSecondary: theme._mediumEmphasisColor,
	inactive: theme._inactiveColor,
	accent: theme._secondaryColor,
	warning: theme._warningColor,
});

export const isNoPayOverrideActive = override => {
	if (override === true) return true;
	if (!override || typeof override !== 'object') return false;
	return (
		override.active === true ||
		override.waived === true ||
		override.enabled === true ||
		override.status === 'active'
	);
};

export const getSourceCompletedCodeLabel = sourceCompletedCode => {
	if (
		typeof sourceCompletedCode === 'string' ||
		typeof sourceCompletedCode === 'number'
	)
		return String(sourceCompletedCode);
	if (!sourceCompletedCode || typeof sourceCompletedCode !== 'object')
		return '';
	const { id, name } = sourceCompletedCode;
	if (name && id) return `${name} (#${id})`;
	return name || (id ? `#${id}` : 'Código no-pay');
};

const MANAGEMENT_REASON_LABELS = {
	NOT_APPLICABLE: 'La restricción no aplica a esta orden.',
	ORDER_NOT_BILLED: 'La orden aún no está facturada.',
	ORDER_ALREADY_PAID: 'La orden ya fue pagada.',
	ORDER_ASSOCIATED_TO_PAYMENT:
		'La orden ya está asociada a un pago y no puede modificarse.',
	FORBIDDEN: 'No tiene autorización para gestionar esta restricción.',
};

const Panel = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
	width: 100%;
`;

const Copy = styled.p`
	color: ${({ $tokens }) => $tokens.textSecondary};
	font-size: 13px;
	line-height: 1.4;
	margin: 3px 0;
`;

const Status = styled.span`
	align-self: flex-start;
	border: 1px solid
		${({ $blocking, $active, $tokens }) =>
			$blocking
				? $tokens.warning
				: $active
					? $tokens.accent
					: $tokens.inactive};
	border-radius: 999px;
	color: ${({ $blocking, $active, $tokens }) =>
		$blocking
			? $tokens.warning
			: $active
				? $tokens.accent
				: $tokens.textSecondary};
	font-size: 10px;
	font-weight: 750;
	line-height: 1;
	margin-bottom: 4px;
	padding: 5px 7px;
	text-transform: uppercase;
`;

const Meta = styled.div`
	color: ${({ $tokens }) => $tokens.textSecondary};
	display: flex;
	flex-wrap: wrap;
	font-size: 12px;
	gap: 4px 10px;
	line-height: 1.4;
`;

const ErrorTitle = styled.strong`
	color: ${({ $tokens }) => $tokens.textPrimary};
	font-size: 13px;
`;

const ActionRow = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: 9px;
`;

const Form = styled.form`
	display: flex;
	flex-direction: column;
	gap: 10px;
	padding: 8px 0 0;
`;

const Label = styled.label`
	color: ${({ $tokens }) => $tokens.textPrimary};
	font-weight: 700;
`;

const Help = styled.span`
	color: ${({ $invalid, $tokens }) =>
		$invalid ? $tokens.warning : $tokens.textSecondary};
	font-size: 12px;
`;

const Footer = styled.div`
	display: flex;
	gap: 8px;
	justify-content: flex-end;
	margin-top: 8px;
`;

export default function NoPayRestrictionControl({
	restriction,
	loadError,
	onSubmit,
	onError,
}) {
	const { theme } = useTheme();
	const themeTokens = getNoPayRestrictionThemeTokens(theme);
	const [mode, setMode] = useState();
	const [reason, setReason] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const submittingRef = useRef(false);
	const lifecycleRef = useRef(0);

	const overrideActive = isNoPayOverrideActive(restriction?.override);
	const sourceCompletedCodeLabel = getSourceCompletedCodeLabel(
		restriction?.sourceCompletedCode,
	);
	const applicable = restriction?.applicable === true;
	const blocking =
		applicable &&
		(restriction?.blocking === true || restriction?.effectiveNoPay === true);
	const canManage = restriction?.management?.canManage === true;
	const nextMode = overrideActive ? 'restore' : blocking ? 'waive' : undefined;
	const normalizedReason = reason.trim();
	const reasonTooShort =
		normalizedReason.length > 0 && normalizedReason.length < MIN_REASON_LENGTH;
	const reasonTooLong = normalizedReason.length > MAX_REASON_LENGTH;
	const invalidReason = !normalizedReason || reasonTooShort || reasonTooLong;
	const canSubmit =
		canManage && mode === nextMode && !invalidReason && !submitting;
	const actionLabel =
		nextMode === 'restore' ? 'Restaurar restricción no-pay' : 'Permitir pago';
	const modalTitle =
		mode === 'restore'
			? 'Restaurar restricción no-pay'
			: 'Permitir pago de la orden';

	useEffect(() => {
		lifecycleRef.current += 1;
		submittingRef.current = false;
		setSubmitting(false);
		setMode(undefined);
		setReason('');
		return () => {
			lifecycleRef.current += 1;
		};
	}, [restriction]);

	const closeModal = () => {
		if (submittingRef.current) return;
		setMode(undefined);
		setReason('');
	};

	const handleSubmit = async event => {
		event.preventDefault();
		if (!canSubmit || submittingRef.current || !mode) return;
		const lifecycle = lifecycleRef.current;
		submittingRef.current = true;
		setSubmitting(true);
		try {
			await onSubmit({
				waived: mode === 'waive',
				reason: normalizedReason,
			});
			if (lifecycle !== lifecycleRef.current) return;
			submittingRef.current = false;
			setSubmitting(false);
			setMode(undefined);
			setReason('');
		} catch (error) {
			if (lifecycle !== lifecycleRef.current) return;
			submittingRef.current = false;
			setSubmitting(false);
			if (onError) onError(error);
		}
	};

	if (loadError)
		return (
			<Panel aria-live='polite'>
				<ErrorTitle $tokens={themeTokens}>
					No se pudo cargar la restricción no-pay
				</ErrorTitle>
				<Copy $tokens={themeTokens} role='alert'>
					{loadError}
				</Copy>
			</Panel>
		);

	if (!restriction)
		return (
			<Panel aria-live='polite' aria-busy='true'>
				<Copy $tokens={themeTokens}>Cargando restricción no-pay…</Copy>
			</Panel>
		);

	return (
		<Panel aria-label='Estado del código no pagable'>
			<Status
				role='status'
				$blocking={blocking && !overrideActive}
				$active={overrideActive || (applicable && !blocking)}
				$tokens={themeTokens}
				data-no-pay-status
			>
				{!applicable
					? 'No aplica'
					: overrideActive
						? 'Excepción administrativa activa'
						: blocking
							? 'Bloquea el pago'
							: 'No bloquea el pago'}
			</Status>
			<Meta $tokens={themeTokens}>
				{sourceCompletedCodeLabel && (
					<span>
						Código: <strong>{sourceCompletedCodeLabel}</strong>
					</span>
				)}
			</Meta>
			{overrideActive && restriction.override?.reason && (
				<Copy $tokens={themeTokens}>Razón: {restriction.override.reason}</Copy>
			)}
			{!canManage && restriction.management?.reason && (
				<Copy $tokens={themeTokens}>
					{MANAGEMENT_REASON_LABELS[restriction.management.reason] ||
						restriction.management.reason}
				</Copy>
			)}

			{canManage && applicable && nextMode && (
				<ActionRow>
					<AntButton
						aria-label={
							nextMode === 'restore'
								? 'Restaurar restricción por código no-pay'
								: 'Permitir pago pese al código no-pay'
						}
						type={nextMode === 'waive' ? 'primary' : 'default'}
						size='small'
						onClick={() => setMode(nextMode)}
					>
						{actionLabel}
					</AntButton>
				</ActionRow>
			)}

			<Modal
				visible={!!mode}
				title={modalTitle}
				aria-label={modalTitle}
				footer={null}
				destroyOnClose
				onCancel={closeModal}
				closable={!submitting}
				maskClosable={!submitting}
			>
				<Form onSubmit={handleSubmit} noValidate>
					<Copy $tokens={themeTokens}>
						{mode === 'restore'
							? 'La orden volverá a quedar sujeta al código no-pay.'
							: 'La orden podrá pagarse aunque haya sido cerrada con un código no-pay.'}
					</Copy>
					<Label $tokens={themeTokens} htmlFor='no-pay-restriction-reason'>
						Razón
					</Label>
					<TextField.TextArea
						id='no-pay-restriction-reason'
						value={reason}
						onChange={event => setReason(event.target.value)}
						disabled={submitting}
						aria-invalid={reasonTooShort || reasonTooLong}
						aria-describedby='no-pay-restriction-reason-help'
						rows={4}
					/>
					<Help
						id='no-pay-restriction-reason-help'
						$invalid={reasonTooShort || reasonTooLong}
						$tokens={themeTokens}
						role={reasonTooShort || reasonTooLong ? 'alert' : undefined}
					>
						{reasonTooShort
							? `Ingrese al menos ${MIN_REASON_LENGTH} caracteres.`
							: reasonTooLong
								? `Ingrese como máximo ${MAX_REASON_LENGTH} caracteres.`
								: `${normalizedReason.length}/${MAX_REASON_LENGTH} caracteres (mínimo ${MIN_REASON_LENGTH}).`}
					</Help>
					<Footer>
						<AntButton
							htmlType='button'
							onClick={closeModal}
							disabled={submitting}
						>
							Cancelar
						</AntButton>
						<AntButton
							type='primary'
							htmlType='submit'
							loading={submitting}
							disabled={!canSubmit}
							data-action='confirm'
						>
							Confirmar
						</AntButton>
					</Footer>
				</Form>
			</Modal>
		</Panel>
	);
}
